import { FC, HTMLProps, ReactElement, useEffect } from 'react';
import styles from './Scan2OrderPopUp.module.scss';

import { generateId } from 'util/generateId';
import { useRefHandleFocus } from 'hooks/useHandleFocus';
import { VisualHeaderLayout } from 'components/ModalKit/VisualHeaderLayout/VisualHeaderLayout';
import { StackedCtaLayout } from 'components/ModalKit/Cta/StackedCtaLayout';
import CtaItem from 'components/ModalKit/Cta/CtaItem';
import { ThreePartLayout } from 'components/Layout/ThreePartLayout/ThreePartLayout';
import { ModalKitModal } from 'components/ModalKit/ModalKitModal/ModalKitModal';
import { ModalKitCloseButton } from 'components/ModalKit/ModalKitCloseButton/ModalKitCloseButton';
import Typography from './Typography/Typography';
import { Banner, BannerVariant } from './Banner';
import { Icon } from './Icon';
import { Trans, useTranslation } from 'react-i18next';
import { heapTrack } from 'util/Analytics';
import { useServiceType } from 'hooks/context/serviceType';

const ENABLE_SHARING_BANNER = false;

export type Scan2OrderPopUpProps = HTMLProps<HTMLElement> & {
  isOpen: boolean;
  className?: string;
  title: string;
  onClose: () => void;
  testId?: string;
  logo: ReactElement;
};

export const Scan2OrderPopUp: FC<Scan2OrderPopUpProps> = ({
  isOpen,
  onClose,
  title,
  testId = 's2o-pop-up',
  logo
}) => {

  const { t } = useTranslation();
  const labelId = generateId();
  const [ref] = useRefHandleFocus(isOpen);
  const { hasTableId } = useServiceType();

  useEffect(() => {
    if (isOpen) {
      heapTrack('S2O-welcome-page-visit');
    }
  }, [isOpen]);

  const handleClick = () => {
    heapTrack('S2O-welcome-click-continue');
    onClose();
  };

  //for scan 2 order we have decided not to support desktop, there will only be a mobileLayout
  const welcomeContent = (
    <ThreePartLayout className={styles.mobileLayout}>
      <ThreePartLayout.Header>
        <VisualHeaderLayout className={styles.header}>
          <VisualHeaderLayout.Visual>
            {logo}
          </VisualHeaderLayout.Visual>
          <VisualHeaderLayout.Title>
            <Typography.Heading1 id={labelId}>
              {title}
            </Typography.Heading1>
          </VisualHeaderLayout.Title>
        </VisualHeaderLayout>
      </ThreePartLayout.Header>
      <ThreePartLayout.Main>
        <div className={styles.content}>
          <p>
            <Trans i18nKey="scan2order.subtitle">
              Ordering on your phone is easy.<br />Add items to your order and send them to the kitchen to get started.
            </Trans>
          </p>
          <p>
            <Trans i18nKey="scan2order.content">
              Want a 2nd course?<br />You can always add more later.
            </Trans>
          </p>
        </div>
      </ThreePartLayout.Main>
      <ThreePartLayout.Footer>
        {ENABLE_SHARING_BANNER && (
          <Banner variant={BannerVariant.INFO} className={styles.banner} rounded={true}>
            <Icon aria-hidden={true} name={'peopleWoman'} size={24} />
            <Icon aria-hidden={true} name={'peopleMan'} size={24} />
            <div className={styles.infoWrapper}>
              <p className={styles.bold}>{t('scan2order.banner.title')}</p>
              <p>{t('scan2order.banner.content')}</p>
            </div>
          </Banner>
        )}
        <StackedCtaLayout className={styles.footer}>
          <CtaItem.Primary onClick={handleClick} ref={ref} >
            {t('scan2order.continue')}
          </CtaItem.Primary>
        </StackedCtaLayout>
      </ThreePartLayout.Footer>
    </ThreePartLayout>
  );

  //in the case where there is no table id, render an error
  const errorContent = (
    <ThreePartLayout className={styles.mobileLayout}>
      <ThreePartLayout.Header>
        <VisualHeaderLayout className={styles.header}>
          <VisualHeaderLayout.Visual>
            <Icon name={'sobbing'} className={styles.icon} size={64} />
          </VisualHeaderLayout.Visual>
          <VisualHeaderLayout.Title>
            <Typography.Heading1 id={labelId}>
            <p>{t('scan2order.error.title')}</p>
            </Typography.Heading1>
          </VisualHeaderLayout.Title>
        </VisualHeaderLayout>
      </ThreePartLayout.Header>
      <ThreePartLayout.Main>
        <div className={styles.content}>
          <p>{t('scan2order.error.content')}</p>
        </div>
      </ThreePartLayout.Main>
    </ThreePartLayout>
  );

  return (
    <ModalKitModal
      aria={{ labelledby: labelId }}
      isOpen={isOpen}
      onClose={onClose}
      testId={testId}
    >
      {onClose && <ModalKitCloseButton onClick={onClose} />}
      {hasTableId && welcomeContent}
      {!hasTableId && errorContent}
    </ModalKitModal>
  );
};
